import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { pdfjs } from "react-pdf";
import moment from "moment";
import { backendUrl } from "../../constants";
import PublicPageHeader from "../../components/PublicPageHeader";

// Public signing page: the token in the URL is the only credential.
// Never log it or send it anywhere other than the e-sign API.
const API_BASE = (backendUrl || "").replace(/\/+$/, "");
const PDF_SCALE = 1.3;
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
const ALLOWED_UPLOAD_TYPES = ["image/png", "image/jpeg"];
const VALID_DATA_URL = /^data:image\/(png|jpeg);base64,/;

export default function ESignDocument() {
  const { token } = useParams();
  const signUrl = `${API_BASE}/api/v2/esign/sign/${encodeURIComponent(token || "")}`;

  const [pageState, setPageState] = useState("loading"); // loading | invalid | sign | done
  const [invalidMessage, setInvalidMessage] = useState("");
  const [doc, setDoc] = useState(null);
  const [completion, setCompletion] = useState(null);

  const [pdfPages, setPdfPages] = useState([]);
  const [pdfError, setPdfError] = useState("");
  const pdfDocRef = useRef(null);
  const pageCanvasRefs = useRef({});

  const [tab, setTab] = useState("draw");
  const padRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [uploadDataUrl, setUploadDataUrl] = useState("");
  const uploadInputRef = useRef(null);
  const signatureSectionRef = useRef(null);

  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  // Metadata call also marks the document as opened, so it runs once per mount
  // (the ref guard keeps StrictMode's double effect from calling it twice).
  const metadataRequestedRef = useRef(false);
  useEffect(() => {
    if (metadataRequestedRef.current) return;
    metadataRequestedRef.current = true;

    fetch(signUrl)
      .then((res) => res.json())
      .then((json) => {
        if (!json || json.status !== true) {
          setInvalidMessage((json && typeof json.data === "string" && json.data) || "Signing link is invalid.");
          setPageState("invalid");
          return;
        }
        const data = json.data;
        setDoc(data);
        if (data.status === "COMPLETED") {
          setCompletion({
            completedAt: data.completedAt,
            downloadUrl: (data.pdfUrl || "").replace(/\/pdf$/, "/download"),
          });
          setPageState("done");
        } else {
          setPageState("sign");
        }
      })
      .catch(() => {
        setInvalidMessage("We couldn't load this document. Please check your connection and open the link again.");
        setPageState("invalid");
      });
  }, [signUrl]);

  // Load the PDF and read each page's size so the wrappers can be laid out.
  useEffect(() => {
    if (pageState !== "sign" || !doc?.pdfUrl) return;
    let cancelled = false;

    fetch(`${API_BASE}${doc.pdfUrl}`)
      .then((res) => {
        if (!res.ok) throw new Error("pdf");
        return res.arrayBuffer();
      })
      .then((buffer) => pdfjs.getDocument({ data: buffer }).promise)
      .then(async (pdf) => {
        if (cancelled) {
          pdf.destroy();
          return;
        }
        pdfDocRef.current = pdf;
        const pages = [];
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: PDF_SCALE });
          pages.push({ pageNumber: i, width: viewport.width, height: viewport.height });
        }
        if (!cancelled) setPdfPages(pages);
      })
      .catch(() => {
        if (!cancelled) setPdfError("We couldn't load the document preview. Please reload the page.");
      });

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [pageState, doc]);

  // Draw each page into its canvas once the wrappers are mounted.
  useEffect(() => {
    const pdf = pdfDocRef.current;
    if (!pdf || !pdfPages.length) return;
    const dpr = window.devicePixelRatio || 1;
    const tasks = [];

    pdfPages.forEach(async ({ pageNumber }) => {
      const canvas = pageCanvasRefs.current[pageNumber];
      if (!canvas) return;
      try {
        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: PDF_SCALE });
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        const task = page.render({
          canvasContext: canvas.getContext("2d"),
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
        });
        tasks.push(task);
        await task.promise;
      } catch (e) {
        // Render cancelled on unmount, or a single page failed; leave it blank.
      }
    });

    return () => tasks.forEach((task) => task.cancel());
  }, [pdfPages]);

  // Signature pad: size the backing store by devicePixelRatio so strokes stay sharp and aligned.
  const setupPad = useCallback(() => {
    const canvas = padRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
    setHasInk(false);
  }, []);

  useEffect(() => {
    if (pageState !== "sign" || tab !== "draw") return;
    setupPad();
    window.addEventListener("resize", setupPad);
    return () => window.removeEventListener("resize", setupPad);
  }, [pageState, tab, setupPad]);

  const padPoint = (e) => {
    const rect = padRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const onPadPointerDown = (e) => {
    e.preventDefault();
    padRef.current.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const ctx = padRef.current.getContext("2d");
    const { x, y } = padPoint(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    // A single tap still leaves a dot.
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    setHasInk(true);
    setFormError("");
  };

  const onPadPointerMove = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = padRef.current.getContext("2d");
    const { x, y } = padPoint(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const onPadPointerUp = () => {
    drawingRef.current = false;
  };

  const clearPad = () => {
    const canvas = padRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setHasInk(false);
  };

  const removeUpload = () => {
    setUploadDataUrl("");
    if (uploadInputRef.current) uploadInputRef.current.value = "";
  };

  const onUploadChange = (e) => {
    const file = e.target.files && e.target.files[0];
    setFormError("");
    if (!file) return;
    if (!ALLOWED_UPLOAD_TYPES.includes(file.type)) {
      removeUpload();
      setFormError("Please upload a PNG or JPEG image.");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      removeUpload();
      setFormError("Signature image must be 2 MB or smaller.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      if (!VALID_DATA_URL.test(result)) {
        removeUpload();
        setFormError("Please upload a PNG or JPEG image.");
        return;
      }
      setUploadDataUrl(result);
    };
    reader.onerror = () => {
      removeUpload();
      setFormError("We couldn't read that file. Please try another image.");
    };
    reader.readAsDataURL(file);
  };

  const scrollToSignature = () => {
    signatureSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async () => {
    if (submitting) return;
    setFormError("");

    let signatureDataUrl = "";
    if (tab === "draw") {
      if (!hasInk || !padRef.current) {
        setFormError("Please draw your signature before submitting.");
        return;
      }
      signatureDataUrl = padRef.current.toDataURL("image/png");
    } else {
      if (!uploadDataUrl) {
        setFormError("Please upload an image of your signature before submitting.");
        return;
      }
      signatureDataUrl = uploadDataUrl;
    }

    if (!consent) {
      setFormError("Please tick the consent box to continue.");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${signUrl}/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signatureDataUrl,
          signatureMethod: tab === "draw" ? "drawn" : "uploaded",
          consent: true,
        }),
      });
      const json = await res.json();
      if (!json || json.status !== true) {
        setFormError((json && typeof json.data === "string" && json.data) || "We couldn't apply your signature. Please try again.");
        setSubmitting(false);
        return;
      }
      setCompletion({
        completedAt: json.data.completedAt,
        downloadUrl: json.data.downloadUrl,
      });
      setPageState("done");
      window.scrollTo(0, 0);
    } catch (e) {
      setFormError("We couldn't reach the server. Please check your connection and try again.");
      setSubmitting(false);
    }
  };

  const field = doc?.field;

  return (
    <div className="quotationPreviewWrapper esignPage">
      <PublicPageHeader />

      <div className="container esignContainer">
        {pageState === "loading" && (
          <div className="esignCard esignCenter">
            <div className="spinner-border text-primary" role="status" />
            <p className="esignMuted mt-3 mb-0">Loading document…</p>
          </div>
        )}

        {pageState === "invalid" && (
          <div className="esignCard esignCenter">
            <div className="esignStatusIcon esignStatusIconError">
              <i className="fa-solid fa-link-slash"></i>
            </div>
            <h2 className="esignTitle">Link unavailable</h2>
            <p className="esignMuted mb-0">{invalidMessage}</p>
          </div>
        )}

        {pageState === "done" && completion && (
          <div className="esignCard esignCenter" id="esign-completed" data-esign-status="COMPLETED">
            <div className="esignStatusIcon esignStatusIconSuccess">
              <i className="fa-solid fa-check"></i>
            </div>
            <h2 className="esignTitle">Document signed</h2>
            <p className="esignMuted">
              Your signature has been applied{doc?.documentName ? <> to <strong>{doc.documentName}</strong></> : null} and
              a completion certificate page has been appended to the document.
            </p>
            {completion.completedAt && (
              <p className="esignMeta">
                Completed on {moment(completion.completedAt).format("D MMM YYYY, h:mm A")}
              </p>
            )}
            {completion.downloadUrl && (
              <a
                className="btn btn-primary esignBtn"
                href={`${API_BASE}${completion.downloadUrl}`}
                download
              >
                <i className="fa-solid fa-download me-2"></i>Download signed PDF
              </a>
            )}
            <p className="esignMuted esignSmall mt-3 mb-0">
              A signed copy has also been emailed to {doc?.recipientEmail ? <strong>{doc.recipientEmail}</strong> : "you"}.
            </p>
          </div>
        )}

        {pageState === "sign" && doc && (
          <>
            <div className="esignCard">
              <h2 className="esignTitle mb-1">{doc.documentName || doc.filename}</h2>
              <p className="esignMuted mb-0">
                {doc.recipientName ? <>Hi {doc.recipientName}, please </> : "Please "}
                review the document below and sign in the highlighted box.
              </p>
            </div>

            <div className="esignDocument">
              {pdfError && <div className="esignError">{pdfError}</div>}
              {!pdfError && !pdfPages.length && (
                <div className="esignCenter py-5">
                  <div className="spinner-border text-primary" role="status" />
                  <p className="esignMuted mt-3 mb-0">Loading preview…</p>
                </div>
              )}
              {pdfPages.map(({ pageNumber, width }) => (
                <div key={pageNumber} className="esignPdfPage" style={{ maxWidth: width }}>
                  <canvas ref={(el) => { pageCanvasRefs.current[pageNumber] = el; }} />
                  {field && Number(field.page) === pageNumber && (
                    <button
                      type="button"
                      className={`esignField ${(tab === "draw" ? hasInk : uploadDataUrl) ? "esignFieldReady" : ""}`}
                      style={{
                        left: field.x * 100 + "%",
                        top: field.y * 100 + "%",
                        width: field.width * 100 + "%",
                        height: field.height * 100 + "%",
                      }}
                      onClick={scrollToSignature}
                    >
                      Sign here
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="esignCard" ref={signatureSectionRef}>
              <h3 className="esignSubtitle">Your signature</h3>

              <div className="esignTabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "draw"}
                  className={`esignTab ${tab === "draw" ? "active" : ""}`}
                  onClick={() => { setTab("draw"); setFormError(""); }}
                >
                  Draw
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={tab === "upload"}
                  className={`esignTab ${tab === "upload" ? "active" : ""}`}
                  onClick={() => { setTab("upload"); setFormError(""); }}
                >
                  Upload
                </button>
              </div>

              {tab === "draw" && (
                <div>
                  <canvas
                    ref={padRef}
                    className="esignPad"
                    onPointerDown={onPadPointerDown}
                    onPointerMove={onPadPointerMove}
                    onPointerUp={onPadPointerUp}
                    onPointerCancel={onPadPointerUp}
                    onPointerLeave={onPadPointerUp}
                  />
                  <div className="esignPadActions">
                    <span className="esignMuted esignSmall">Draw your signature with your mouse or finger.</span>
                    <button type="button" className="btn btn-link p-0" onClick={clearPad} disabled={!hasInk}>
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {tab === "upload" && (
                <div>
                  {!uploadDataUrl ? (
                    <label className="esignUpload">
                      <input
                        ref={uploadInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={onUploadChange}
                      />
                      <i className="fa-solid fa-upload"></i>
                      <span>Choose a PNG or JPEG image (max 2 MB)</span>
                    </label>
                  ) : (
                    <div className="esignUploadPreview">
                      <img src={uploadDataUrl} alt="Uploaded signature" />
                      <button type="button" className="btn btn-link p-0" onClick={removeUpload}>
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              )}

              <label className="esignConsent">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => { setConsent(e.target.checked); setFormError(""); }}
                />
                <span>
                  I agree to use this electronic signature for this document and confirm that I am the intended recipient.
                </span>
              </label>

              {formError && <div className="esignError">{formError}</div>}

              <button
                type="button"
                className="btn btn-primary esignBtn w-100"
                onClick={onSubmit}
                disabled={submitting}
              >
                {submitting ? "Signing…" : "Sign document"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
